import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import {
  useGetConsultantQualityItemEditorDataQuery as useGetEditorDataQuery,
  ConsultantQualityItemEditor_ClearStatus as clearStatusFn,
  ConsultantEventLog_GetEvents as getConsultantEvents,
  ConsultantSearchCard_Get
} from "@bonliva-crm/graphql";
import { DefaultValues, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import locale from "@bonliva-crm/locale";
import { useState } from "react";
import { useFileUploader } from "@consultants/consultant-profile-new/files";
import { getUtcDate, CommonFormData, UseQualityItemEditorParams } from "./utils";

export function useQualityItemEditor<T extends CommonFormData = CommonFormData>({
  country,
  consultantId,
  itemName,
  schema,
  getStatusParams,
  onClose
}: UseQualityItemEditorParams<T>) {
  const snackbar = useSnackbarsPipeline();
  const [_loading, setLoading] = useState(false);
  const fileUploader = useFileUploader({ consultantId });

  const form = useForm<T>({
    defaultValues: {
      date: getUtcDate()
    } as DefaultValues<T>,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const getDataQuery = useGetEditorDataQuery({
    variables: {
      consultantId,
      itemName,
      country
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const { Status: status, DocumentType } = data.getConsultantQualityItem;

      if (DocumentType) {
        fileUploader.setDocumentType(DocumentType);
      }

      if (status) {
        const params = getStatusParams(data.getConsultantQualityItem.Status);

        form.reset({
          date: getUtcDate(),
          comment: status.Comment ?? "",
          ...params
        } as T);
      }
    }
  });

  async function wrapPromise(promise: Promise<any>) {
    try {
      setLoading(true);
      await promise;

      snackbar.showSuccess(locale.Saved);

      onClose();
    } catch (err) {
      snackbar.showError(`Cannot save.\nReason: ${err}`);
    } finally {
      setLoading(false);
    }
  }

  const data = getDataQuery.data?.getConsultantQualityItem;
  const availableStatuses = data?.AvailableStatuses;
  const error = getDataQuery.error;
  const loading = getDataQuery.loading || _loading;
  const name = data?.Name;
  const withDocument = availableStatuses?.some((x) => x.WithDocument);

  function clearStatus() {
    return wrapPromise(
      clearStatusFn.func(
        {
          consultantId,
          country,
          qualityCheckName: itemName
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );
  }

  return {
    form,
    loading,
    data,
    name,
    error,
    withDocument,
    fileUploader,
    clearStatus,
    setLoading
  };
}
